<template>
  <div class="activity-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <h2>营销活动管理</h2>
        <div class="store-info">
          <i class="el-icon-shop"></i>
          <span>{{ storeName }}</span>
        </div>
      </div>
      <div class="header-right">
        <el-button type="primary" size="medium" @click="openCreateDialog">
          <i class="el-icon-plus"></i> 新建活动
        </el-button>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-cards">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-card" :class="{ active: searchParams.status === null }" @click="filterByStatus(null)">
            <div class="stat-value">{{ stats.total || 0 }}</div>
            <div class="stat-label">全部活动</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card draft" :class="{ active: searchParams.status === 1 }" @click="filterByStatus(1)">
            <div class="stat-value">{{ stats.draft || 0 }}</div>
            <div class="stat-label">草稿</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card active" :class="{ active: searchParams.status === 2 }" @click="filterByStatus(2)">
            <div class="stat-value">{{ stats.active || 0 }}</div>
            <div class="stat-label">进行中</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card ended" :class="{ active: searchParams.status === 3 }" @click="filterByStatus(3)">
            <div class="stat-value">{{ stats.ended || 0 }}</div>
            <div class="stat-label">已结束</div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
          v-model="searchParams.name"
          placeholder="搜索活动名称"
          prefix-icon="el-icon-search"
          clearable
          style="width: 260px"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
      />
      <el-select v-model="searchParams.activityType" placeholder="活动类型" clearable style="width: 140px" @change="handleSearch">
        <el-option label="全部" value="" />
        <el-option label="仅积分活动" value="points" />
        <el-option label="仅折扣活动" value="discount" />
        <el-option label="积分+折扣" value="both" />
      </el-select>
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      <el-button @click="resetFilter" icon="el-icon-refresh-right">重置</el-button>
    </div>

    <!-- 活动列表 -->
    <div class="activity-list" v-loading="loading">
      <div class="activity-grid" v-if="activityList.length > 0">
        <div
            v-for="activity in activityList"
            :key="activity.id"
            class="activity-card"
            :class="{
            'status-draft': activity.status === 1,
            'status-active': activity.status === 2,
            'status-ended': activity.status === 3,
            'status-cancelled': activity.status === 4
          }"
        >
          <div class="status-badge">
            <el-tag :type="getStatusType(activity.status)" size="small" effect="dark">
              {{ getStatusText(activity.status) }}
            </el-tag>
          </div>

          <div class="card-content">
            <div class="activity-image" v-if="activity.bannerImage">
              <img :src="activity.bannerImage" />
            </div>
            <div class="activity-name">{{ activity.name }}</div>
            <div class="activity-desc" v-if="activity.description">{{ activity.description }}</div>

            <div class="activity-rules">
              <div class="rule-item" v-if="activity.enablePoints">
                <i class="el-icon-star-on"></i>
                <span class="rule-label">积分活动</span>
                <el-tag size="mini" type="success">{{ activity.pointsMultiple }}倍积分</el-tag>
              </div>
              <div class="rule-item" v-if="activity.enableDiscount">
                <i class="el-icon-s-promotion"></i>
                <span class="rule-label">折扣活动</span>
                <el-tag size="mini" type="warning">
                  <span v-if="activity.discountType === 1">减 {{ formatMoney(activity.discountValue) }}元</span>
                  <span v-else>{{ activity.discountValue }}折</span>
                </el-tag>
              </div>
              <div class="rule-item" v-if="!activity.enablePoints && !activity.enableDiscount">
                <span class="no-rule">未配置活动规则</span>
              </div>
            </div>

            <div class="limit-info">
              <div class="limit-item" v-if="activity.purchaseLimit > 0">
                <i class="el-icon-user"></i>
                <span>每人限购 {{ activity.purchaseLimit }} 件</span>
              </div>
              <div class="limit-item" v-if="activity.totalLimit > 0">
                <i class="el-icon-goods"></i>
                <span>总限购 {{ activity.totalLimit }} 件</span>
                <span class="sold-info">已售 {{ activity.soldCount || 0 }} 件</span>
              </div>
              <div class="limit-item" v-if="activity.purchaseLimit === 0 && activity.totalLimit === 0">
                <i class="el-icon-info"></i>
                <span>无限购</span>
              </div>
            </div>

            <div class="time-info">
              <div class="time-range">
                <i class="el-icon-date"></i>
                {{ formatDateShort(activity.startTime) }} 至 {{ formatDateShort(activity.endTime) }}
              </div>
              <div class="time-status" :class="getTimeStatusClass(activity)">
                {{ getTimeStatusText(activity) }}
              </div>
            </div>

            <div class="stats-info">
              <div class="stat-item">
                <span class="stat-num">{{ activity.participantCount || 0 }}</span>
                <span class="stat-text">参与人数</span>
              </div>
              <div class="stat-item">
                <span class="stat-num">{{ activity.combinationCount || 0 }}</span>
                <span class="stat-text">参与套餐</span>
              </div>
            </div>
          </div>

          <div class="card-actions">
            <el-button type="text" size="small" @click="viewDetail(activity)">
              <i class="el-icon-view"></i> 详情
            </el-button>
            <el-button type="text" size="small" @click="editActivity(activity)">
              <i class="el-icon-edit"></i> 编辑
            </el-button>
            <el-button type="text" size="small" class="text-info" @click="useAsTemplate(activity)">
              <i class="el-icon-copy-document"></i> 套用模板
            </el-button>
            <el-button v-if="activity.status === 1" type="text" size="small" class="text-success" @click="publishActivity(activity)">
              <i class="el-icon-check"></i> 发布
            </el-button>
            <el-button v-if="activity.status === 2" type="text" size="small" class="text-warning" @click="endActivity(activity)">
              <i class="el-icon-remove"></i> 结束
            </el-button>
            <el-button type="text" size="small" class="text-danger" @click="deleteActivity(activity)">
              <i class="el-icon-delete"></i> 删除
            </el-button>
          </div>
        </div>
      </div>

      <el-empty v-else description="暂无活动，点击右上角「新建活动」开始创建" :image-size="120" />

      <div class="pagination-container" v-if="activityList.length > 0">
        <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[12, 24, 48]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            background
        />
      </div>
    </div>

    <!-- 创建/编辑弹窗 -->
    <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        width="750px"
        append-to-body
        :close-on-click-modal="false"
        class="activity-dialog"
        @close="handleDialogClose"
    >
      <div class="template-bar" v-if="!isEdit">
        <span class="template-label">套用模板：</span>
        <el-select
            v-model="templateId"
            placeholder="选择已有活动作为模板（可选）"
            clearable
            filterable
            style="width: 280px"
            @change="applyTemplate"
        >
          <el-option
              v-for="activity in templateList"
              :key="activity.id"
              :label="activity.name"
              :value="activity.id"
          >
            <span style="float: left">{{ activity.name }}</span>
            <span style="float: right; color: #8492a6; font-size: 12px">
              <span v-if="activity.enablePoints">{{ activity.pointsMultiple }}倍积分</span>
              <span v-if="activity.enablePoints && activity.enableDiscount"> | </span>
              <span v-if="activity.enableDiscount">
                <span v-if="activity.discountType === 1">减{{ formatMoney(activity.discountValue) }}元</span>
                <span v-else>{{ activity.discountValue }}折</span>
              </span>
            </span>
          </el-option>
        </el-select>
        <div class="template-hint">
          <i class="el-icon-info"></i> 选择后自动填充活动配置，可在此基础上修改
        </div>
      </div>

      <el-form :model="formData" :rules="formRules" ref="activityFormRef" label-width="110px">
        <el-form-item label="活动名称" prop="name" required>
          <el-input v-model="formData.name" placeholder="请输入活动名称" maxlength="50" show-word-limit />
        </el-form-item>

        <el-form-item label="活动描述">
          <el-input v-model="formData.description" type="textarea" :rows="2" placeholder="请输入活动描述（选填）" maxlength="200" show-word-limit />
        </el-form-item>

        <el-form-item label="活动时间" prop="dateRange" required>
          <el-date-picker
              v-model="formData.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="timestamp"
              style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="活动主图">
          <div class="upload-row">
            <div class="upload-box" @click="openMainImageSelector">
              <template v-if="activityMainImageUrl">
                <img :src="activityMainImageUrl" class="upload-thumb" />
                <div class="upload-mask">
                  <i class="el-icon-delete" @click.stop="removeActivityMainImage"></i>
                </div>
              </template>
              <template v-else>
                <i class="el-icon-plus upload-icon"></i>
                <span>上传主图</span>
              </template>
            </div>
          </div>
          <div class="form-tip">用于活动列表展示</div>
        </el-form-item>

        <el-form-item label="参与套餐" prop="combinationIds" required>
          <el-select
              v-model="formData.combinationIds"
              multiple
              filterable
              placeholder="请选择参与活动的套餐"
              style="width: 100%"
              :loading="combinationLoading"
          >
            <el-option
                v-for="item in combinationList"
                :key="item.id"
                :label="`${item.name} (¥${formatMoney(item.price)})`"
                :value="item.id"
            />
          </el-select>
          <div class="form-tip">至少选择一个套餐，活动才会生效</div>
        </el-form-item>

        <div class="section-title">
          <i class="el-icon-star-on"></i>
          <span>积分活动</span>
          <el-switch v-model="formData.enablePoints" class="section-switch" />
        </div>

        <template v-if="formData.enablePoints">
          <el-form-item label="积分倍数" prop="pointsMultiple">
            <el-input-number v-model="formData.pointsMultiple" :min="0.5" :max="10" :step="0.5" :precision="1" />
            <span class="form-tip">倍（消费1元可得{{ formData.pointsMultiple }}积分）</span>
          </el-form-item>
          <el-form-item label="积分说明">
            <el-input v-model="formData.pointsRemark" placeholder="例如：双倍积分活动" maxlength="100" />
          </el-form-item>
        </template>

        <div class="section-title">
          <i class="el-icon-s-promotion"></i>
          <span>折扣活动</span>
          <el-switch v-model="formData.enableDiscount" class="section-switch" />
        </div>

        <template v-if="formData.enableDiscount">
          <el-form-item label="折扣类型" prop="discountType">
            <el-radio-group v-model="formData.discountType">
              <el-radio :label="1">固定金额优惠</el-radio>
              <el-radio :label="2">百分比折扣</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="优惠值" prop="discountValue">
            <template v-if="formData.discountType === 1">
              <el-input-number v-model="formData.discountValue" :min="1" :max="10000" placeholder="请输入优惠金额" />
              <span class="form-tip">元（如：500表示减5元）</span>
            </template>
            <template v-else>
              <el-input-number v-model="formData.discountValue" :min="1" :max="99" placeholder="请输入折扣" />
              <span class="form-tip">折（如：85表示85折）</span>
            </template>
          </el-form-item>
          <el-form-item label="折扣说明">
            <el-input v-model="formData.discountRemark" placeholder="例如：全场85折" maxlength="100" />
          </el-form-item>
        </template>

        <div class="section-title">
          <i class="el-icon-s-order"></i>
          <span>限购设置</span>
        </div>

        <el-form-item label="每人限购">
          <el-input-number v-model="formData.purchaseLimit" :min="0" :max="999" :step="1" placeholder="0表示不限购" />
          <span class="form-tip">份（每人最多可购买数量，0表示不限）</span>
        </el-form-item>

        <el-form-item label="活动总限购">
          <el-input-number v-model="formData.totalLimit" :min="0" :max="99999" :step="1" placeholder="0表示不限" />
          <span class="form-tip">份（活动总限量，0表示不限）</span>
        </el-form-item>

        <el-form-item label="活动备注">
          <el-input v-model="formData.remark" type="textarea" :rows="2" placeholder="请输入活动备注" maxlength="200" show-word-limit />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitActivity" :loading="submitting">保存</el-button>
      </div>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog
        title="活动详情"
        :visible.sync="detailDialog.visible"
        width="550px"
        append-to-body
        class="detail-dialog"
    >
      <div class="detail-content">
        <div class="detail-header">
          <h3>{{ detailDialog.data.name }}</h3>
          <el-tag :type="getStatusType(detailDialog.data.status)" size="small">
            {{ getStatusText(detailDialog.data.status) }}
          </el-tag>
        </div>
        <p class="detail-desc" v-if="detailDialog.data.description">{{ detailDialog.data.description }}</p>

        <div class="detail-section" v-if="detailDialog.data.bannerImage">
          <div class="detail-label">活动主图</div>
          <div class="detail-value">
            <img :src="detailDialog.data.bannerImage" style="max-width: 200px; border-radius: 8px;" />
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-label">活动时间</div>
          <div class="detail-value">{{ formatDate(detailDialog.data.startTime) }} 至 {{ formatDate(detailDialog.data.endTime) }}</div>
        </div>

        <div class="detail-section">
          <div class="detail-label">参与套餐</div>
          <div class="detail-value">
            <div v-for="(item, idx) in detailDialog.combinations" :key="idx" class="combination-tag">
              <el-tag size="small">{{ item.name }}</el-tag>
            </div>
            <span v-if="!detailDialog.combinations?.length" class="no-data">未选择套餐</span>
          </div>
        </div>

        <div class="detail-section" v-if="detailDialog.data.enablePoints">
          <div class="detail-label">积分活动</div>
          <div class="detail-value">
            <el-tag type="success" size="small">{{ detailDialog.data.pointsMultiple }}倍积分</el-tag>
            <span class="detail-remark" v-if="detailDialog.data.pointsRemark">{{ detailDialog.data.pointsRemark }}</span>
          </div>
        </div>

        <div class="detail-section" v-if="detailDialog.data.enableDiscount">
          <div class="detail-label">折扣活动</div>
          <div class="detail-value">
            <el-tag type="warning" size="small">
              <span v-if="detailDialog.data.discountType === 1">减 {{ formatMoney(detailDialog.data.discountValue) }} 元</span>
              <span v-else>{{ detailDialog.data.discountValue }} 折</span>
            </el-tag>
            <span class="detail-remark" v-if="detailDialog.data.discountRemark">{{ detailDialog.data.discountRemark }}</span>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-label">限购设置</div>
          <div class="detail-value">
            <span v-if="detailDialog.data.purchaseLimit > 0">每人限购 {{ detailDialog.data.purchaseLimit }} 件，</span>
            <span v-if="detailDialog.data.totalLimit > 0">总限购 {{ detailDialog.data.totalLimit }} 件</span>
            <span v-if="detailDialog.data.purchaseLimit === 0 && detailDialog.data.totalLimit === 0">无限购</span>
          </div>
        </div>

        <div class="detail-section" v-if="detailDialog.data.remark">
          <div class="detail-label">备注</div>
          <div class="detail-value">{{ detailDialog.data.remark }}</div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialog.visible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 活动主图选择器 -->
    <image-selector
        v-model="showActivityMainImageSelector"
        :max-count="1"
        @select="handleActivityMainImageSelect"
    />
  </div>
</template>

<script>
import api from "@/utils/api";
import ImageSelector from '@/components/ImageSelector'

export default {
  name: "ActivityList",
  components: { ImageSelector },
  data() {
    return {
      loading: false,
      combinationLoading: false,
      submitting: false,
      storeName: '加载中...',
      storeId: null,

      searchParams: {
        name: '',
        status: null,
        activityType: ''
      },

      stats: {
        total: 0,
        draft: 0,
        active: 0,
        ended: 0
      },

      activityList: [],
      combinationList: [],
      templateList: [],

      pagination: {
        page: 1,
        pageSize: 12,
        total: 0
      },

      dialogVisible: false,
      dialogTitle: '新建活动',
      isEdit: false,
      editId: null,
      templateId: null,

      // 活动主图相关
      showActivityMainImageSelector: false,
      activityMainImageUrl: '',
      activityMainImageId: null,

      formData: {
        name: '',
        description: '',
        dateRange: [],
        combinationIds: [],
        enablePoints: false,
        pointsMultiple: 1,
        pointsRemark: '',
        enableDiscount: false,
        discountType: 1,
        discountValue: null,
        discountRemark: '',
        purchaseLimit: 0,
        totalLimit: 0,
        remark: ''
      },

      formRules: {
        name: [
          { required: true, message: '请输入活动名称', trigger: 'blur' },
          { max: 50, message: '活动名称不能超过50个字符', trigger: 'blur' }
        ],
        dateRange: [
          { required: true, message: '请选择活动时间', trigger: 'change' }
        ],
        combinationIds: [
          { required: true, message: '请选择参与套餐', trigger: 'change', type: 'array', min: 1 }
        ]
      },

      detailDialog: {
        visible: false,
        data: {},
        combinations: []
      }
    };
  },
  created() {
    this.getStoreInfo();
  },
  methods: {
    async getStoreInfo() {
      try {
        const res = await api.login_getAdminInfo();
        const responseData = res.data || res;
        if (responseData.code === '000' && responseData.data) {
          this.storeId = responseData.data.merchantId || responseData.data.id;
          this.storeName = responseData.data.storeName || responseData.data.name || '我的店铺';
          this.loadActivityList();
          this.loadCombinationList();
          this.loadTemplateList();
        } else {
          this.storeName = '获取失败';
        }
      } catch (error) {
        console.error('获取店铺信息异常:', error);
        this.storeName = '获取失败';
      }
    },

    async loadActivityList() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        };

        if (this.searchParams.name) {
          params.name = this.searchParams.name;
        }
        if (this.searchParams.status !== null && this.searchParams.status !== '') {
          params.status = this.searchParams.status;
        }
        if (this.searchParams.activityType === 'points') {
          params.enablePoints = true;
          params.enableDiscount = false;
        } else if (this.searchParams.activityType === 'discount') {
          params.enablePoints = false;
          params.enableDiscount = true;
        } else if (this.searchParams.activityType === 'both') {
          params.enablePoints = true;
          params.enableDiscount = true;
        }

        const res = await api.activity_getList(params);
        const responseData = res.data || res;
        if (responseData && responseData.code === '000') {
          this.activityList = responseData.data?.list || [];
          this.pagination.total = responseData.data?.totalCount || 0;
          this.updateStats();
        }
      } catch (error) {
        console.error('加载活动列表失败:', error);
      } finally {
        this.loading = false;
      }
    },

    updateStats() {
      this.stats = {
        total: this.activityList.length,
        draft: this.activityList.filter(a => a.status === 1).length,
        active: this.activityList.filter(a => a.status === 2).length,
        ended: this.activityList.filter(a => a.status === 3).length
      };
    },

    async loadTemplateList() {
      try {
        const res = await api.activity_getTemplateList();
        const responseData = res.data || res;
        if (responseData && responseData.code === '000') {
          this.templateList = responseData.data || [];
        }
      } catch (error) {
        console.error('加载模板列表失败:', error);
      }
    },

    async loadCombinationList() {
      this.combinationLoading = true;
      try {
        const res = await api.activity_getCombinationList();
        const responseData = res.data || res;
        if (responseData && responseData.code === '000') {
          this.combinationList = responseData.data || [];
        }
      } catch (error) {
        console.error('加载套餐列表失败:', error);
      } finally {
        this.combinationLoading = false;
      }
    },

    prepareSubmitData(data) {
      const submitData = { ...data };
      delete submitData.dateRange;
      submitData.enablePoints = submitData.enablePoints ? 1 : 0;
      if (submitData.enableDiscount === true || submitData.enableDiscount === 1) {
        submitData.enableDiscount = true;
      } else {
        submitData.enableDiscount = false;
        delete submitData.discountType;
        delete submitData.discountValue;
        delete submitData.discountRemark;
      }
      submitData.purchaseLimit = submitData.purchaseLimit || 0;
      submitData.totalLimit = submitData.totalLimit || 0;
      return submitData;
    },

    async applyTemplate(activityId) {
      if (!activityId) return;
      try {
        const res = await api.activity_getDetail({ id: activityId });
        const responseData = res.data || res;
        if (responseData && responseData.code === '000') {
          const template = responseData.data;
          this.activityMainImageId = template.bannerImageId || null;
          this.activityMainImageUrl = template.bannerImage || '';
          this.formData = {
            name: `${template.name}（副本）`,
            description: template.description || '',
            dateRange: [],
            combinationIds: template.combinationIds || [],
            enablePoints: template.enablePoints === 1,
            pointsMultiple: template.pointsMultiple || 1,
            pointsRemark: template.pointsRemark || '',
            enableDiscount: template.enableDiscount === 1,
            discountType: template.discountType || 1,
            discountValue: template.discountValue,
            discountRemark: template.discountRemark || '',
            purchaseLimit: template.purchaseLimit || 0,
            totalLimit: template.totalLimit || 0,
            remark: template.remark || ''
          };
          this.$message.success(`已套用活动「${template.name}」的配置`);
        }
      } catch (error) {
        console.error('套用模板失败:', error);
        this.$message.error('套用模板失败');
      }
    },

    filterByStatus(status) {
      this.searchParams.status = status;
      this.pagination.page = 1;
      this.loadActivityList();
    },

    handleSearch() {
      this.pagination.page = 1;
      this.loadActivityList();
    },

    resetFilter() {
      this.searchParams = { name: '', status: null, activityType: '' };
      this.pagination.page = 1;
      this.loadActivityList();
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.loadActivityList();
    },

    handleCurrentChange(page) {
      this.pagination.page = page;
      this.loadActivityList();
    },

    openCreateDialog() {
      this.dialogTitle = '新建活动';
      this.isEdit = false;
      this.editId = null;
      this.templateId = null;
      this.activityMainImageId = null;
      this.activityMainImageUrl = '';
      this.formData = {
        name: '', description: '', dateRange: [], combinationIds: [],
        enablePoints: false, pointsMultiple: 1, pointsRemark: '',
        enableDiscount: false, discountType: 1, discountValue: null, discountRemark: '',
        purchaseLimit: 0, totalLimit: 0, remark: ''
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.activityFormRef?.clearValidate();
      });
    },

    useAsTemplate(activity) {
      this.openCreateDialog();
      this.templateId = activity.id;
      this.$nextTick(() => {
        this.applyTemplate(activity.id);
      });
    },

    editActivity(row) {
      this.dialogTitle = '编辑活动';
      this.isEdit = true;
      this.editId = row.id;
      this.templateId = null;
      this.activityMainImageId = row.bannerImageId || null;
      this.activityMainImageUrl = row.bannerImage || '';
      this.formData = {
        name: row.name,
        description: row.description || '',
        dateRange: [row.startTime, row.endTime],
        combinationIds: row.combinationIds || [],
        enablePoints: row.enablePoints === 1,
        pointsMultiple: row.pointsMultiple || 1,
        pointsRemark: row.pointsRemark || '',
        enableDiscount: row.enableDiscount === 1,
        discountType: row.discountType || 1,
        discountValue: row.discountValue,
        discountRemark: row.discountRemark || '',
        purchaseLimit: row.purchaseLimit || 0,
        totalLimit: row.totalLimit || 0,
        remark: row.remark || ''
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.activityFormRef?.clearValidate();
      });
    },

    openMainImageSelector() {
      this.showActivityMainImageSelector = true;
    },

    handleActivityMainImageSelect(images) {
      if (images && images.length) {
        this.activityMainImageId = images[0].id;
        this.activityMainImageUrl = images[0].url;
      }
      this.showActivityMainImageSelector = false;
    },

    removeActivityMainImage() {
      this.activityMainImageId = null;
      this.activityMainImageUrl = '';
    },

    async viewDetail(row) {
      try {
        const res = await api.activity_getDetail({ id: row.id });
        const responseData = res.data || res;
        if (responseData && responseData.code === '000') {
          this.detailDialog.data = responseData.data;
          this.detailDialog.combinations = responseData.data.combinations || [];
          this.detailDialog.visible = true;
        }
      } catch (error) {
        console.error('获取详情失败:', error);
      }
    },

    async submitActivity() {
      this.submitting = true;
      try {
        const valid = await this.$refs.activityFormRef.validate();
        if (!valid) return;

        if (!this.formData.dateRange || this.formData.dateRange.length !== 2) {
          this.$message.warning('请选择活动时间');
          return;
        }

        if (this.formData.combinationIds.length === 0) {
          this.$message.warning('请选择参与套餐');
          return;
        }

        if (this.formData.enableDiscount && !this.formData.discountValue) {
          this.$message.warning('请输入优惠值');
          return;
        }

        const baseData = {
          name: this.formData.name,
          description: this.formData.description,
          bannerImageId: this.activityMainImageId,
          startTime: this.formData.dateRange[0],
          endTime: this.formData.dateRange[1],
          combinationIds: this.formData.combinationIds,
          enablePoints: this.formData.enablePoints,
          pointsMultiple: this.formData.pointsMultiple,
          pointsRemark: this.formData.pointsRemark,
          enableDiscount: this.formData.enableDiscount,
          discountType: this.formData.discountType,
          discountValue: this.formData.discountValue,
          discountRemark: this.formData.discountRemark,
          purchaseLimit: this.formData.purchaseLimit,
          totalLimit: this.formData.totalLimit,
          remark: this.formData.remark
        };

        const params = this.prepareSubmitData(baseData);

        let res;
        if (this.isEdit) {
          res = await api.activity_update({ id: this.editId, ...params });
        } else {
          res = await api.activity_create(params);
        }

        const responseData = res.data || res;
        if (responseData && responseData.code === '000') {
          this.$message.success(this.isEdit ? '修改成功' : '创建成功');
          this.dialogVisible = false;
          this.loadActivityList();
          this.loadTemplateList();
        } else {
          this.$message.error(responseData?.message || (this.isEdit ? '修改失败' : '创建失败'));
        }
      } catch (e) {
        console.error('提交失败:', e);
        this.$message.error('提交失败');
      } finally {
        this.submitting = false;
      }
    },

    async publishActivity(row) {
      this.submitting = true;
      try {
        const res = await api.activity_publish({ id: row.id });
        const responseData = res.data || res;
        if (responseData && responseData.code === '000') {
          this.$message.success('发布成功');
          this.loadActivityList();
          this.loadTemplateList();
        } else {
          this.$message.error(responseData?.message || '发布失败');
        }
      } catch (e) {
        console.error('发布失败:', e);
        this.$message.error('发布失败');
      } finally {
        this.submitting = false;
      }
    },

    async endActivity(row) {
      this.submitting = true;
      try {
        const res = await api.activity_end({ id: row.id });
        const responseData = res.data || res;
        if (responseData && responseData.code === '000') {
          this.$message.success('已结束');
          this.loadActivityList();
          this.loadTemplateList();
        } else {
          this.$message.error(responseData?.message || '结束失败');
        }
      } catch (e) {
        console.error('结束失败:', e);
        this.$message.error('结束失败');
      } finally {
        this.submitting = false;
      }
    },

    async deleteActivity(row) {
      this.$confirm(`确定要删除活动"${row.name}"吗？删除后不可恢复！`, '警告', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await api.activity_delete({ id: row.id });
          const responseData = res.data || res;
          if (responseData && responseData.code === '000') {
            this.$message.success('删除成功');
            this.loadActivityList();
            this.loadTemplateList();
          } else {
            this.$message.error(responseData?.message || '删除失败');
          }
        } catch (e) {
          console.error('删除失败:', e);
          this.$message.error('删除失败');
        }
      }).catch(() => {});
    },

    handleDialogClose() {
      this.activityMainImageId = null;
      this.activityMainImageUrl = '';
      this.formData = {
        name: '', description: '', dateRange: [], combinationIds: [],
        enablePoints: false, pointsMultiple: 1, pointsRemark: '',
        enableDiscount: false, discountType: 1, discountValue: null, discountRemark: '',
        purchaseLimit: 0, totalLimit: 0, remark: ''
      };
      this.templateId = null;
    },

    getStatusType(status) {
      const map = { 1: 'info', 2: 'success', 3: 'info', 4: 'danger' };
      return map[status] || 'info';
    },

    getStatusText(status) {
      const map = { 1: '草稿', 2: '进行中', 3: '已结束', 4: '已取消' };
      return map[status] || '未知';
    },

    getTimeStatusClass(activity) {
      const now = Date.now();
      if (activity.status !== 2) return '';
      if (now < activity.startTime) return 'time-not-started';
      if (now > activity.endTime) return 'time-ended';
      return 'time-active';
    },

    getTimeStatusText(activity) {
      const now = Date.now();
      if (activity.status !== 2) return '';
      if (now < activity.startTime) return '未开始';
      if (now > activity.endTime) return '已过期';
      return '进行中';
    },

    formatMoney(amount) {
      if (!amount && amount !== 0) return '0';
      return (amount / 100).toFixed(2);
    },

    formatDate(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    },

    formatDateShort(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .header-left {
    display: flex;
    align-items: baseline;
    gap: 16px;
    h2 { margin: 0; font-size: 20px; font-weight: 600; color: #303133; }
    .store-info {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      background: #f0f2f5;
      border-radius: 20px;
      font-size: 13px;
      color: #606266;
      i { font-size: 14px; color: #909399; }
    }
  }
}

.stat-cards { margin-bottom: 20px;
  .stat-card {
    background: #fff;
    border-radius: 12px;
    padding: 16px 20px;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
    &.active { border-left: 4px solid #409eff; background: #ecf5ff; }
    .stat-value { font-size: 28px; font-weight: 700; color: #303133; }
    .stat-label { font-size: 14px; color: #909399; margin-top: 4px; }
    &.draft.active { border-left-color: #909399; background: #f4f4f5; }
    &.active.active { border-left-color: #67c23a; background: #f0f9eb; }
    &.ended.active { border-left-color: #e6a23c; background: #fdf6ec; }
  }
}

.search-bar {
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.activity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
}

.activity-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  position: relative;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12); }
  &.status-draft { border-top: 4px solid #909399; }
  &.status-active { border-top: 4px solid #67c23a; }
  &.status-ended { border-top: 4px solid #e6a23c; }
  &.status-cancelled { border-top: 4px solid #f56c6c; }
  .status-badge { position: absolute; top: 12px; right: 12px; z-index: 1; }
  .card-content { padding: 20px;
    .activity-image {
      width: 100%;
      height: 160px;
      overflow: hidden;
      border-radius: 8px;
      margin-bottom: 12px;
      img { width: 100%; height: 100%; object-fit: cover; }
    }
    .activity-name { font-size: 18px; font-weight: 600; color: #303133; margin-bottom: 8px; padding-right: 60px; line-height: 1.4; }
    .activity-desc { font-size: 13px; color: #909399; margin-bottom: 16px; line-height: 1.5; }
    .activity-rules { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 12px;
      .rule-item { display: flex; align-items: center; gap: 6px;
        i { font-size: 14px; color: #e6a23c; }
        .rule-label { font-size: 12px; color: #606266; }
        .no-rule { font-size: 12px; color: #c0c4cc; }
      }
    }
    .limit-info { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 12px; padding: 8px 12px; background: #f8f9fa; border-radius: 8px;
      .limit-item { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #606266;
        i { font-size: 12px; color: #909399; }
        .sold-info { margin-left: 4px; color: #e6a23c; }
      }
    }
    .time-info { background: #f5f7fa; border-radius: 8px; padding: 10px 12px; margin-bottom: 16px;
      .time-range { font-size: 12px; color: #606266; display: flex; align-items: center; gap: 6px;
        i { color: #909399; }
      }
      .time-status { font-size: 11px; margin-top: 4px;
        &.time-active { color: #67c23a; }
        &.time-not-started { color: #e6a23c; }
        &.time-ended { color: #909399; }
      }
    }
    .stats-info { display: flex; gap: 24px;
      .stat-item { display: flex; align-items: baseline; gap: 6px;
        .stat-num { font-size: 18px; font-weight: 600; color: #409eff; }
        .stat-text { font-size: 12px; color: #909399; }
      }
    }
  }
  .card-actions {
    border-top: 1px solid #ebeef5;
    padding: 12px 20px;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    background: #fafafa;
    .el-button--text { padding: 0;
      i { margin-right: 4px; }
      &.text-success { color: #67c23a; }
      &.text-warning { color: #e6a23c; }
      &.text-danger { color: #f56c6c; }
      &.text-info { color: #409eff; }
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
}

.activity-dialog, .detail-dialog {
  .template-bar {
    background: #f0f9eb;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    .template-label { font-size: 14px; color: #67c23a; font-weight: 500; }
    .template-hint { font-size: 12px; color: #909399; display: flex; align-items: center; gap: 4px;
      i { color: #909399; }
    }
  }
  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 0 8px;
    border-bottom: 1px solid #ebeef5;
    margin-bottom: 16px;
    i { font-size: 18px; color: #409eff; }
    span { font-weight: 500; color: #303133; }
    .section-switch { margin-left: auto; }
  }
  .form-tip { font-size: 12px; color: #909399; margin-left: 12px; }
  .dialog-footer { text-align: right; padding-top: 16px; }
}

.detail-dialog {
  .detail-content {
    .detail-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
      h3 { margin: 0; font-size: 18px; font-weight: 600; }
    }
    .detail-desc { color: #909399; font-size: 13px; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px solid #ebeef5; }
    .detail-section { margin-bottom: 16px;
      .detail-label { font-size: 13px; color: #909399; margin-bottom: 6px; }
      .detail-value { font-size: 14px; color: #303133;
        .combination-tag { display: inline-block; margin-right: 8px; margin-bottom: 4px; }
        .detail-remark { margin-left: 8px; font-size: 12px; color: #909399; }
      }
    }
  }
  .no-data { color: #c0c4cc; font-size: 13px; }
  .dialog-footer { text-align: right; }
}

.upload-row {
  display: flex;
  gap: 12px;
}

.upload-box {
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  background: #fafafa;

  &:hover {
    border-color: #409eff;
  }

  .upload-icon {
    font-size: 24px;
    color: #999;
    margin-bottom: 4px;
  }

  span {
    font-size: 12px;
    color: #999;
  }

  .upload-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .upload-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity .3s;

    i {
      color: #fff;
      font-size: 20px;
    }

    &:hover {
      opacity: 1;
    }
  }
}

@media (max-width: 768px) {
  .activity-page { padding: 12px; }
  .page-header { flex-direction: column; gap: 12px; align-items: flex-start; }
  .activity-grid { grid-template-columns: 1fr; }
  .search-bar { flex-direction: column;
    .el-input, .el-select { width: 100% !important; }
  }
  .stat-cards .el-col { margin-bottom: 12px; }
  .activity-dialog .template-bar { flex-direction: column; align-items: flex-start;
    .el-select { width: 100% !important; }
  }
}

::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
</style>